/**
 * Central news store, topic refresh, and feed proxy resolution.
 */
import { useState } from "@bunnix/core";
import { sources } from "./sources.mjs";
import { fetchRssNews } from "./rss-extractor.mjs";
import { savePreferences, saveTopicNews } from "./persistence.mjs";
import { normalizeNews } from "./news-cache.mjs";

function createInitialState(preloadedPreferences = {}, newsByTopic = {}) {
  return {
    userPreferences: { ...preloadedPreferences },
    newsByTopic,
    loadingByTopic: {},
  };
}

let store = null;
let lastWrittenPrefs = "";

export function initStore(preloadedPreferences = {}, newsByTopic = {}) {
  if (!store) {
    store = useState(createInitialState(preloadedPreferences, newsByTopic));
    lastWrittenPrefs = JSON.stringify(store.get().userPreferences ?? {});
    store.subscribe((snapshot) => {
      const json = JSON.stringify(snapshot.userPreferences ?? {});
      if (json !== lastWrittenPrefs) {
        lastWrittenPrefs = json;
        savePreferences(snapshot.userPreferences ?? {});
      }
    });
  }
  return store;
}

export function isLocalDevHost(hostname = "") {
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") {
    return true;
  }
  const octets = hostname.split(".").map(Number);
  if (octets.length !== 4 || octets.some((n) => !Number.isInteger(n) || n < 0 || n > 255)) {
    return false;
  }
  const [a, b] = octets;
  return a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
}

const FEED_PROXY = "/api/feed?url=";

const topicRequests = new Map();

const updateTopic = async ({ topic, fetchFn = fetchRssNews, proxy } = {}) => {
  const request = (topicRequests.get(topic) ?? 0) + 1;
  topicRequests.set(topic, request);
  const initial = store.get();
  store.set({ ...initial, loadingByTopic: { ...initial.loadingByTopic, [topic]: true } });
  const resolvedProxy = proxy ?? FEED_PROXY;

  const feeds = sources.flatMap((source) => source.feeds
      .filter((feed) => feed.topic === topic)
      .map((feed) => ({ ...feed, source: source.id })));
  const settled = await Promise.allSettled(feeds.map((feed) =>
    fetchFn(feed.rss, { source: feed.source, proxy: resolvedProxy })));
  const failedSources = new Set(feeds.filter((_, index) => settled[index].status === "rejected")
    .map((feed) => feed.source));

  const news = normalizeNews([
    ...settled.flatMap((result) => (result.status === "fulfilled" ? result.value : [])),
    ...(store.get().newsByTopic[topic] ?? []).filter((item) => failedSources.has(item.source)),
  ]);

  if (topicRequests.get(topic) !== request) return news;

  const fresh = store.get();
  store.set({
    ...fresh,
    newsByTopic: { ...fresh.newsByTopic, [topic]: news },
    loadingByTopic: { ...fresh.loadingByTopic, [topic]: false },
  });

  await saveTopicNews(topic, news);

  return news;
}

function persistSelection(kind, id) {
  const snapshot = store.get();
  store.set({
    ...snapshot,
    userPreferences: { ...snapshot.userPreferences, lastSelection: { kind, id } },
  });
}

export function useStore() {
  if (!store) initStore();
  return { store, updateTopic, persistSelection }
}
